import {
  Component,
  Inject,
  OnInit,
  OnDestroy,
  OnChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Input,
} from '@angular/core';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { SiteParams } from '@types';
import { WidenService } from '@services/widen.service';
import { getMonthDiffrence } from '@marketing/marketing-utils';
import { SearchServiceToken } from '@frk/eds-components';
import { FtSearchService } from '@search/services/ftsearch.service';
import { AppStateService } from '@services/app-state.service';
import { GlobalConfigService } from '@services/global-config-service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { FtSearchOptions } from '@search/interfaces/search.interface';
import { HttpParams } from '@angular/common/http';
import { ConfigService } from '@search/services/config.service';
import { FiltersService } from '@search/services/filters.service';
import { FtInsightsItem } from '@search/interfaces/insights.interface';
import {
  InsightsContentList,
  ArticleContent,
  DisplayParams,
} from '@marketing/insights/insights.interface';
import { InsightsService } from '@marketing/insights/insights.service';

/**
 * FT Insights Cards component
 *
 * Selector: `ft-insights-cards`
 *
 * Exported as: `InsightsCardsComponent`
 */

@Component({
  selector: 'ft-insights-cards',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './insights-cards.component.html',
  providers: [InsightsService],
})
export class InsightsCardsComponent
  extends AbstractBaseComponent
  implements OnInit, OnDestroy, OnChanges {
  content: InsightsContentList;
  componentDestroyed$: Subject<boolean> = new Subject();
  dynamicArticles: FtInsightsItem[] = [];
  @Input() externalPreFilters: DisplayParams;
  @Input() externalInsightResource: any;

  /**
   * SIM logo resource bundle object from parent component
   */
  @Input() externalSIMLogoResource: object;
  translationLabel: any;
  viewMoreURL: string;
  viewMoreLabel: string;
  isShowInsightCard: boolean;
  options: FtSearchOptions = {
    page: 0,
    term: '*',
    dataSource: 'dev',
    articleType: 'insight',
    config: {
      ftSearchUrl: '',
      ftInsightsUrl: '',
      httpParams: new HttpParams(),
      httpOptions: {},
    },
    // TODO verify if we use it and move to config
    counters: {
      funds: 4,
      literature: 4,
      pages: 3,
    },
  };

  /**
   * Site config
   */
  siteParams: SiteParams;

  /**
   * SIM logo resource bundle object
   */
  simLogoResourceBundle: object;

  /**
   * Constructor
   */
  constructor(
    private widenService: WidenService,
    private configService: ConfigService,
    private filters: FiltersService,
    private cd: ChangeDetectorRef,
    @Inject(SearchServiceToken) private searchService: FtSearchService,
    private appStateService: AppStateService,
    private insightsService: InsightsService,
    private pageConfigService: GlobalConfigService
  ) {
    super();
    this.siteParams = this.pageConfigService.getSiteParams();
  }

  /**
   * Angular init hook
   */
  ngOnInit(): void {
    super.ngOnInit();
    this.translationLabel = this.component?.getModels()[
      this.displayParams?.resourceBundles
    ];

    if (!this.externalSIMLogoResource) {
      this.simLogoResourceBundle = this.component?.getModels()[
        'ft.core.sim-logos'
      ];
    } else {
      this.simLogoResourceBundle = this.externalSIMLogoResource;
    }

    this.viewMoreLabel = this.translationLabel?.viewMore;
    let insightsBasePath = this.translationLabel?.insightsHubPath;

    if (!insightsBasePath) {
      insightsBasePath = this.externalInsightResource?.insightsHubPath;
    }

    if (!this.viewMoreLabel) {
      this.viewMoreLabel = this.externalInsightResource?.viewMore;
    }

    this.setViewMoreURL(insightsBasePath);

    if (this.displayParams?.document) {
      this.content = this.getCuratedContent();
    } else {
      this.searchService.insightsCardOutput$
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe(({ results }) => {
          this.dynamicArticles = results.response?.hits?.hits;
          this.content = this.getDynamicContent();
          this.cd.detectChanges();
        });
      this.configService
        .getConfig('insights')
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe((config) => {
          this.options.config = config;
          if (this.displayParams?.maximumCards) {
            this.options.counters.pages = this.displayParams.maximumCards;
          }
          this.setPreFilter();
          const searchParams = {
            options: this.options,
            filters: this.filters.getFilters(),
          };
          this.searchService.getInsightsCard(searchParams);
        });
    }
  }

  /**
   * Get content from document
   */
  getCuratedContent(): InsightsContentList {
    const content = this.document?.getData<InsightsContentList>();
    if (content?.articles?.length) {
      content.articleContent = [];
      for (const article of content.articles) {
        let articleContent: ArticleContent;
        articleContent = this.page
          .getContent(article)
          .getData<ArticleContent>();
        articleContent.href = this.page.getContent(article)?.getUrl();
        articleContent.investmentteam =
          articleContent?.insightMetadata?.investmentteam;
        articleContent.assetclassPage =
          articleContent?.insightMetadata?.assetclass;
        articleContent.investmenttheme =
          articleContent?.insightMetadata?.investmenttheme;
        articleContent.publicationseries =
          articleContent?.insightMetadata?.publicationseries;
        articleContent.speciality = articleContent?.insightMetadata?.speciality;
        articleContent.date = this.insightsService.getLongDate(
          articleContent.isodate
        );
        if (!articleContent.doNotArchive) {
          if (getMonthDiffrence(articleContent.date) > 15) {
            articleContent.isArchived = true;
          }
        }
        if (articleContent?.insightMetadata?.logo) {
          articleContent.SIM = this.insightsService.getSIMLogo(
            articleContent?.insightMetadata?.logo,
            true,
            this.simLogoResourceBundle
          );
        }
        if (articleContent.image?.widenDocument) {
          articleContent.imgSrc = this.getImageUrl(
            articleContent?.image?.widenDocument
          );
        }
        content.articleContent?.push(articleContent);
      }
    }
    return content;
  }

  /**
   * Get dynamic article cards
   */
  getDynamicContent() {
    const dynamicContent: InsightsContentList = {};
    if (this.dynamicArticles?.length) {
      dynamicContent.articleContent = [];
      this.dynamicArticles.forEach((article: FtInsightsItem) => {
        const articleContent: ArticleContent = {};
        articleContent.title = article._source?.title;
        articleContent.investmentteam = article._source?.investmentteam;
        articleContent.assetclassPage = article._source?.assetclassPage;
        articleContent.investmenttheme = article._source?.investmenttheme;
        articleContent.publicationseries = article._source?.publicationseries;
        articleContent.speciality = article._source?.speciality;
        articleContent.date = this.insightsService.getLongDate(
          article._source?.referenceDate
        );
        articleContent.imgSrc = this.getImageUrl(article._source?.image);
        articleContent.SIM = this.insightsService.getSIMLogo(
          article._source?.logo,
          true,
          this.simLogoResourceBundle
        );
        articleContent.cardSummary = article._source?.cardSummary;
        if (getMonthDiffrence(article._source?.referenceDate) > 15) {
          articleContent.isArchived = true;
        }
        if (article._source?.relativePath?.startsWith('/')) {
          articleContent.href =
            this.appStateService?.apiUrls?.spaBaseUrl +
            article._source?.relativePath;
        } else {
          articleContent.href =
            this.appStateService?.apiUrls?.spaBaseUrl +
            '/' +
            article._source?.relativePath;
        }
        dynamicContent.articleContent.push(articleContent);
      });
    }
    return dynamicContent;
  }

  private setViewMoreURL(insightsBasePath: string): void {
    this.viewMoreURL = this.appStateService?.apiUrls?.spaBaseUrl;
    if (this.siteParams.insightCardInfo?.viewMoreUrl) {
      this.viewMoreURL += this.siteParams.insightCardInfo.viewMoreUrl;
      return;
    }
    if (this.displayParams?.viewMoreUrl) {
      this.viewMoreURL += this.displayParams.viewMoreUrl;
      return;
    }
    this.viewMoreURL += insightsBasePath;
  }

  /**
   * Set pre filters for search
   */
  setPreFilter() {
    let insightsPath = '?filter=';
    this.filters.activeFilters = [];
    let filters: DisplayParams;
    if (this.externalPreFilters) {
      filters = this.externalPreFilters;
    } else {
      filters = this.displayParams;
    }

    if (filters) {
      const keys = [
        'assetclass',
        'investmentteam',
        'investmenttheme',
        'publicationseries',
        'speciality',
      ];
      Object.entries(filters).forEach(([key, value]) => {
        if (keys.includes(key.toLowerCase())) {
          if (value) {
            const arr = [];
            arr.push({ value });
            if (key === 'assetClass' || key === 'assetclass') {
              this.filters.addFilter('assetclassPage.exact', arr);
              insightsPath += 'assetclasspage:' + arr[0]?.value + ';';
            } else {
              this.filters.addFilter(key.toLowerCase() + '.exact', arr);
              insightsPath += key?.toLowerCase() + ':' + arr[0]?.value + ';';
            }
          }
        }
      });
      this.viewMoreURL += insightsPath;
    }
  }

  /**
   * Get image src
   */
  getImageUrl(widenObject: any) {
    if (widenObject) {
      return this.widenService.getWidenImageVariantUrl(
        JSON.parse(widenObject).url,
        'original',
        'webp',
        'eds-card-article'
      );
    }
  }

  /**
   * Get paramaters of the component
   */
  get displayParams(): DisplayParams {
    if (this.externalPreFilters) {
      return this.externalPreFilters;
    } else {
      return this.component?.getParameters();
    }
  }

  ngOnDestroy(): void {
    // unsubscribe from Observables
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
